/**
 * Public audio: the one source for every listener who isn't a fully set-up account.
 *
 * Owner decision, 2026-10-13: signed-out visitors hear FULL-LENGTH previews, with no watermark and
 * no cap. So does everyone else who gets the signed-out view (an unfinished sign-up, a blocked
 * account: `src/lib/account-setup.ts`). A fully set-up account keeps the clean file through the
 * signed-in path (`/api/tracks/signed-audio`), as before; `src/hooks/useListeningPath.ts` decides
 * which.
 *
 * `GET /api/tracks/{id}/preview-audio` is public (no session) and answers `200 { url, format,
 * expires_in }`: a signed link to the whole track, valid for `expires_in` seconds. It plays to the
 * end. Anything else (404 for an unknown or unpublished track, a 202, a 503 or any other status, a
 * body without a URL, the network) is "Preview unavailable", and nothing falls back to another
 * source. A URL that stops working while it plays (it expired) is asked for again by the player,
 * which picks up where it was.
 *
 * A preview is kept per track while its URL is good (`expires_in`, less a margin), so the tap that
 * plays a row can start it at once: iOS starts audio only from inside a tap, and a request between
 * the tap and `play()` loses it. Rows ask for theirs as they come on screen
 * (`prefetchPublicPreview`, through `usePublicPreviewPrefetch`).
 *
 * The call goes to the API base, never to a relative `/api/...` path. On Vercel every path except
 * the site's own functions is rewritten to `index.html`, so a relative call gets the app's HTML
 * with a 200 and the player silently has no audio (F23). It works locally only because the Vite
 * dev server proxies `/api`. `src/test/no-relative-api-fetch.test.ts` keeps it that way.
 *
 * Plain `fetch`, not `apiClient`: the endpoint needs no session, and `apiClient` asks Supabase for
 * one and warns about its absence on every anonymous visit.
 */

/** No preview to play: an unknown or unpublished track, an error, or no answer. */
export const PREVIEW_UNAVAILABLE_LABEL = "Preview unavailable";

/**
 * What a signed-out listener reads while a preview plays: the whole track is there; downloading and
 * licensing it take an account.
 */
export const SIGNED_OUT_PREVIEW_CAPTION = "Preview · Sign in to download or license";

/** An answer without `expires_in` is held to the endpoint's old lifetime. */
const DEFAULT_URL_SECONDS = 60;
/** A URL is handed out until this long before it expires: a tenth of its life, 10 s at least. */
const URL_MARGIN_SECONDS = 10;

/** How soon a track whose preview was unavailable is asked about again when it comes on screen.
 *  Pressing Play always asks. */
const UNAVAILABLE_RECHECK_MS = 60_000;

/** Prefetches running at once, and waiting; the oldest waiting row is dropped beyond that. */
const PREFETCH_CONCURRENCY = 3;
const PREFETCH_QUEUE_LIMIT = 24;

/** Tracks remembered at once; the oldest is forgotten beyond that. */
const CACHE_LIMIT = 500;

export interface PublicPreview {
  trackId: string;
  /** A signed link to the whole track. */
  url: string;
  /** When the URL stops being handed out, in ms since the epoch. */
  freshUntil: number;
}

/** A 200 body as a preview, or null when it carries no URL. */
function readPreview(trackId: string, body: unknown, now: number): PublicPreview | null {
  if (!body || typeof body !== "object") return null;
  const { url, expires_in: expiresIn } = body as { url?: unknown; expires_in?: unknown };
  if (typeof url !== "string" || !url.trim()) return null;
  const lifetime =
    typeof expiresIn === "number" && Number.isFinite(expiresIn) && expiresIn > 0
      ? expiresIn
      : DEFAULT_URL_SECONDS;
  const margin = Math.max(URL_MARGIN_SECONDS, lifetime / 10);
  return { trackId, url, freshUntil: now + Math.max(0, lifetime - margin) * 1000 };
}

/**
 * One request for a track's preview: the preview, or null when there is none to play. Every way it
 * can go wrong is null, never a throw: a non-2xx status (a 202 included), a body that isn't JSON
 * (such as the app's own `index.html`), a body without a `url`, a network error. Each is logged,
 * except an aborted call. The API base is read at call time.
 */
export async function fetchPublicPreview(
  trackId: string,
  signal?: AbortSignal
): Promise<PublicPreview | null> {
  if (!trackId.trim()) return null;
  const endpoint = `${import.meta.env.VITE_API_BASE_URL || ""}/api/tracks/${encodeURIComponent(trackId)}/preview-audio`;

  try {
    const response = await fetch(endpoint, { signal });
    // 2xx but not 200: a 202 has no audio to hand out.
    if (response.status !== 200) {
      console.warn(`Preview audio unavailable for track ${trackId}: HTTP ${response.status}`);
      return null;
    }
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      console.warn(`Preview audio unavailable for track ${trackId}: the response isn't JSON`);
      return null;
    }
    const preview = readPreview(trackId, body, Date.now());
    if (!preview) console.warn(`Preview audio unavailable for track ${trackId}: the response has no url`);
    return preview;
  } catch (error) {
    if (!signal?.aborted) console.warn(`Preview audio unavailable for track ${trackId}:`, error);
    return null;
  }
}

type CacheEntry =
  | { status: "ready"; preview: PublicPreview }
  /** Not asked about again by a prefetch before `until`. */
  | { status: "unavailable"; until: number };

const cache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<PublicPreview | null>>();
const prefetchQueue: string[] = [];
const prefetching = new Set<string>();

function remember(trackId: string, preview: PublicPreview | null) {
  cache.delete(trackId);
  cache.set(
    trackId,
    preview
      ? { status: "ready", preview }
      : { status: "unavailable", until: Date.now() + UNAVAILABLE_RECHECK_MS }
  );
  if (cache.size > CACHE_LIMIT) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
}

/** One request per track at a time, whoever asks; its answer is remembered. Never aborted. */
function requestPreview(trackId: string): Promise<PublicPreview | null> {
  const pending = inFlight.get(trackId);
  if (pending) return pending;
  const request: Promise<PublicPreview | null> = fetchPublicPreview(trackId)
    .catch(() => null)
    .then((preview) => {
      // A request from before clearPublicPreviews() answers into nothing.
      if (inFlight.get(trackId) === request) {
        inFlight.delete(trackId);
        remember(trackId, preview);
      }
      return preview;
    });
  inFlight.set(trackId, request);
  return request;
}

/** The answer, or null as soon as `signal` aborts, whatever the request is still doing. */
function unlessAborted(
  request: Promise<PublicPreview | null>,
  signal?: AbortSignal
): Promise<PublicPreview | null> {
  if (!signal) return request;
  if (signal.aborted) return Promise.resolve(null);
  return new Promise((resolve) => {
    const onAbort = () => resolve(null);
    signal.addEventListener("abort", onAbort, { once: true });
    void request.then((preview) => {
      signal.removeEventListener("abort", onAbort);
      resolve(preview);
    });
  });
}

/** A track's preview, already fetched and still good to hand out; otherwise null. */
export function peekPublicPreview(trackId: string): PublicPreview | null {
  const entry = cache.get(trackId);
  if (entry?.status !== "ready") return null;
  if (entry.preview.freshUntil <= Date.now()) {
    cache.delete(trackId);
    return null;
  }
  return entry.preview;
}

/** Drops what is known about a track's preview, so the next request asks afresh (a URL that failed). */
export function forgetPublicPreview(trackId: string): void {
  cache.delete(trackId);
}

export interface LoadPublicPreviewOptions {
  /** Stops waiting. A request already sent still completes and is remembered for the next caller. */
  signal?: AbortSignal;
  /** Asks afresh even when a preview is remembered: its URL failed to play. */
  fresh?: boolean;
}

/**
 * A track's preview to play, or null when there is none (unavailable, no answer) or the caller
 * stopped waiting. A remembered preview whose URL is still good is returned without a request.
 */
export async function loadPublicPreview(
  trackId: string,
  { signal, fresh = false }: LoadPublicPreviewOptions = {}
): Promise<PublicPreview | null> {
  if (fresh) forgetPublicPreview(trackId);
  const remembered = peekPublicPreview(trackId);
  if (remembered) return remembered;
  return unlessAborted(requestPreview(trackId), signal);
}

function wantsPrefetch(trackId: string): boolean {
  if (inFlight.has(trackId) || prefetching.has(trackId)) return false;
  const entry = cache.get(trackId);
  if (!entry) return true;
  if (entry.status === "ready") return entry.preview.freshUntil <= Date.now();
  return entry.until <= Date.now();
}

function runPrefetches() {
  while (prefetching.size < PREFETCH_CONCURRENCY && prefetchQueue.length > 0) {
    const trackId = prefetchQueue.shift() as string;
    if (!wantsPrefetch(trackId)) continue;
    prefetching.add(trackId);
    void requestPreview(trackId).then(() => {
      prefetching.delete(trackId);
      runPrefetches();
    });
  }
}

/**
 * Asks for a track's preview ahead of a tap, so the tap can start it at once. One request, a few
 * at a time; a track already known about isn't asked about again until that knowledge runs out.
 */
export function prefetchPublicPreview(trackId: string): void {
  if (!trackId.trim() || !wantsPrefetch(trackId) || prefetchQueue.includes(trackId)) return;
  prefetchQueue.push(trackId);
  if (prefetchQueue.length > PREFETCH_QUEUE_LIMIT) prefetchQueue.shift();
  runPrefetches();
}

/** Forgets every preview and every waiting prefetch: a clean slate, as each test starts with. */
export function clearPublicPreviews(): void {
  cache.clear();
  inFlight.clear();
  prefetching.clear();
  prefetchQueue.length = 0;
}
